import { Buffer } from 'buffer';
import {
  AccountCreateTransaction,
  AccountId,
  AccountUpdateTransaction,
  ContractExecuteTransaction,
  Hbar,
  KeyList,
  PublicKey,
  ScheduleCreateTransaction,
  ScheduleSignTransaction,
  Timestamp,
  TokenAssociateTransaction,
  TransactionId,
  TransactionReceiptQuery,
  TransferTransaction,
} from '@hashgraph/sdk';
import type { SigningStrategy } from '../signing/SigningStrategy';
import { withRetry } from './circuitBreaker';

/**
 * Convert a decimal amount to raw integer units using string manipulation
 * to avoid floating-point precision errors (e.g., 1.1 * 10^8 = 110000000.00000001).
 */
function toRawAmount(amount: number, decimals: number): number {
  const amountStr = amount.toString();
  const [intPart, decPart = ''] = amountStr.split('.');
  const paddedDecimal = decPart.padEnd(decimals, '0').slice(0, decimals);
  const rawBigInt = BigInt(intPart + paddedDecimal);
  if (rawBigInt > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new Error('Amount exceeds safe integer precision');
  }
  return Number(rawBigInt);
}

function extractScheduleIds(receipt: any): { scheduleId: string; txId: string } {
  const scheduleId = receipt.scheduleId?.toString();
  const txId = receipt.scheduledTransactionId?.toString();
  if (!scheduleId) throw new Error('Expected scheduleId in receipt');
  if (!txId) throw new Error('Expected scheduledTransactionId in receipt');
  return { scheduleId, txId };
}

/**
 * Helper: freeze with client (for custom network node IDs), sign with signer, execute with retry.
 * This pattern replaces freezeWithSigner/executeWithSigner which doesn't work with SDK Wallet
 * on custom networks because Wallet doesn't carry the custom node map.
 */
async function freezeSignExecute(
  tx: any,
  strategy: SigningStrategy,
): Promise<{ transactionId: any }> {
  const signer = strategy.getSigner();
  const client = strategy.getClient();

  const frozenTx = await tx.freezeWith(client);
  await frozenTx.signWithSigner(signer);
  return withRetry(() => frozenTx.execute(client)) as Promise<{
    transactionId: any;
  }>;
}

export async function createMultisigAccount(
  strategy: SigningStrategy,
  publicKeyList: any[],
  threshold: number,
  memo?: string,
) {
  const thresholdKey = new KeyList(publicKeyList, threshold);

  const transaction = new AccountCreateTransaction()
    .setKey(thresholdKey)
    .setAccountMemo(memo ?? '')
    .setInitialBalance(new Hbar(1));

  const response = await freezeSignExecute(transaction, strategy);
  return response.transactionId;
}

export async function createSendHbarScheduledTx(
  strategy: SigningStrategy,
  tx: { from: string; to: string; amount: number },
  expirationTime?: Date,
  waitForExpiry: boolean = false,
): Promise<{ scheduleId: string; txId: string }> {
  const transferTransaction = new TransferTransaction()
    .addHbarTransfer(tx.from, new Hbar(-1 * tx.amount))
    .addHbarTransfer(tx.to, new Hbar(tx.amount));

  const uniqueMemo = `multisig-${Date.now()}`;

  let scheduledTx = new ScheduleCreateTransaction()
    .setScheduledTransaction(transferTransaction)
    .setPayerAccountId(AccountId.fromString(tx.from))
    .setScheduleMemo(uniqueMemo);

  const effectiveExpiration = expirationTime || new Date(Date.now() + 62 * 24 * 60 * 60 * 1000);
  scheduledTx = scheduledTx.setExpirationTime(Timestamp.fromDate(effectiveExpiration));

  if (waitForExpiry) {
    scheduledTx = scheduledTx.setWaitForExpiry(true);
  }

  const txResponse = await freezeSignExecute(scheduledTx, strategy);

  const client = strategy.getClient();
  const receipt = await new TransactionReceiptQuery()
    .setTransactionId(txResponse.transactionId)
    .execute(client);

  return extractScheduleIds(receipt);
}

export async function createTokenTransferScheduledTx(
  strategy: SigningStrategy,
  tx: {
    from: string;
    to: string;
    amount: number;
    tokenId: string;
    decimals: number;
  },
  expirationTime?: Date,
  waitForExpiry: boolean = false,
): Promise<{ scheduleId: string; txId: string }> {
  const adjustedAmount = toRawAmount(tx.amount, tx.decimals);

  const transferTx = new TransferTransaction()
    .addTokenTransfer(tx.tokenId, tx.from, -adjustedAmount)
    .addTokenTransfer(tx.tokenId, tx.to, adjustedAmount);

  const uniqueMemo = `multisig-token-${Date.now()}`;

  let scheduledTx = new ScheduleCreateTransaction()
    .setScheduledTransaction(transferTx)
    .setPayerAccountId(AccountId.fromString(tx.from))
    .setScheduleMemo(uniqueMemo);

  const effectiveExpiration = expirationTime || new Date(Date.now() + 62 * 24 * 60 * 60 * 1000);
  scheduledTx = scheduledTx.setExpirationTime(Timestamp.fromDate(effectiveExpiration));

  if (waitForExpiry) {
    scheduledTx = scheduledTx.setWaitForExpiry(true);
  }

  const txResponse = await freezeSignExecute(scheduledTx, strategy);

  const client = strategy.getClient();
  const receipt = await new TransactionReceiptQuery()
    .setTransactionId(txResponse.transactionId)
    .execute(client);

  return extractScheduleIds(receipt);
}

export async function prepareTokenAssociateTransaction(
  strategy: SigningStrategy,
  accountId: string,
  tokenId: string,
  expirationTime?: Date,
): Promise<{ frozenTxBytes: string; signedTxBytes: string; publicKey: string }> {
  const signer = strategy.getSigner();
  const client = strategy.getClient();
  const publicKey = strategy.getPublicKey();

  const associateTx = new TokenAssociateTransaction()
    .setAccountId(accountId)
    .setTokenIds([tokenId]);

  // Set transaction ID with the multisig account and specified validStart.
  // Token Association uses manual multi-sig (not scheduled), so the validStartTime
  // determines the 180-second execution window. The backend waits until this time
  // arrives before merging signatures and executing.
  if (expirationTime) {
    const validStartTimestamp = Timestamp.fromDate(expirationTime);
    const transactionId = TransactionId.withValidStart(
      AccountId.fromString(accountId),
      validStartTimestamp,
    );
    associateTx.setTransactionId(transactionId);
  } else {
    const transactionId = TransactionId.generate(AccountId.fromString(accountId));
    associateTx.setTransactionId(transactionId);
  }

  const frozenTx = await associateTx.freezeWith(client);
  const frozenBytes = frozenTx.toBytes();
  const signedTx = await frozenTx.signWithSigner(signer);
  const signedBytes = signedTx.toBytes();

  return {
    frozenTxBytes: Buffer.from(frozenBytes).toString('base64'),
    signedTxBytes: Buffer.from(signedBytes).toString('base64'),
    publicKey,
  };
}

export async function signTokenAssociateTransaction(
  strategy: SigningStrategy,
  currentSignedTxBytes: string,
): Promise<{ signedTxBytes: string; publicKey: string }> {
  const signer = strategy.getSigner();
  const publicKey = strategy.getPublicKey();
  const txBytes = Buffer.from(currentSignedTxBytes, 'base64');
  const transaction = TokenAssociateTransaction.fromBytes(txBytes);
  const signedTx = await transaction.signWithSigner(signer);

  return {
    signedTxBytes: Buffer.from(signedTx.toBytes()).toString('base64'),
    publicKey,
  };
}

export async function createContractExecuteScheduledTx(
  strategy: SigningStrategy,
  contractId: string,
  functionData: Uint8Array,
  gas: number,
  payableAmount: number,
  payerAccountId: string,
  expirationTime?: Date,
  waitForExpiry: boolean = false,
): Promise<{ scheduleId: string; txId: string }> {
  const contractTx = new ContractExecuteTransaction()
    .setContractId(contractId)
    .setFunctionParameters(functionData)
    .setGas(gas)
    .setPayableAmount(new Hbar(payableAmount));

  const uniqueMemo = `multisig-contract-${Date.now()}`;

  let scheduledTx = new ScheduleCreateTransaction()
    .setScheduledTransaction(contractTx)
    .setPayerAccountId(AccountId.fromString(payerAccountId))
    .setScheduleMemo(uniqueMemo);

  const effectiveExpiration = expirationTime || new Date(Date.now() + 62 * 24 * 60 * 60 * 1000);
  scheduledTx = scheduledTx.setExpirationTime(Timestamp.fromDate(effectiveExpiration));

  if (waitForExpiry) {
    scheduledTx = scheduledTx.setWaitForExpiry(true);
  }

  const txResponse = await freezeSignExecute(scheduledTx, strategy);

  const client = strategy.getClient();
  const receipt = await new TransactionReceiptQuery()
    .setTransactionId(txResponse.transactionId)
    .execute(client);

  return extractScheduleIds(receipt);
}

export async function createThresholdUpdateScheduledTx(
  strategy: SigningStrategy,
  accountId: string,
  newPublicKeys: any[],
  newThreshold: number,
  payerAccountId: string,
  expirationTime?: Date,
  waitForExpiry: boolean = false,
): Promise<{ scheduleId: string; txId: string }> {
  const keys = newPublicKeys.map((k) => (typeof k === 'string' ? PublicKey.fromString(k) : k));
  const newKey = new KeyList(keys, newThreshold);

  const updateTx = new AccountUpdateTransaction().setAccountId(accountId).setKey(newKey);

  const uniqueMemo = `multisig-update-${Date.now()}`;

  let scheduledTx = new ScheduleCreateTransaction()
    .setScheduledTransaction(updateTx)
    .setPayerAccountId(AccountId.fromString(payerAccountId))
    .setScheduleMemo(uniqueMemo);

  const effectiveExpiration = expirationTime || new Date(Date.now() + 62 * 24 * 60 * 60 * 1000);
  scheduledTx = scheduledTx.setExpirationTime(Timestamp.fromDate(effectiveExpiration));

  if (waitForExpiry) {
    scheduledTx = scheduledTx.setWaitForExpiry(true);
  }

  const txResponse = await freezeSignExecute(scheduledTx, strategy);

  const client = strategy.getClient();
  const receipt = await new TransactionReceiptQuery()
    .setTransactionId(txResponse.transactionId)
    .execute(client);

  return extractScheduleIds(receipt);
}

export async function sendHbarToAccount(
  strategy: SigningStrategy,
  accountId: string,
  amount: number,
): Promise<{ txId: string }> {
  const signerAccountId = strategy.getAccountId();

  const sendHbarTx = new TransferTransaction()
    .addHbarTransfer(signerAccountId, new Hbar(-amount))
    .addHbarTransfer(accountId, new Hbar(amount));

  const txResponse = await freezeSignExecute(sendHbarTx, strategy);

  const client = strategy.getClient();
  await new TransactionReceiptQuery().setTransactionId(txResponse.transactionId).execute(client);

  return { txId: txResponse.transactionId.toString() };
}

export async function sendTokenToAccount(
  strategy: SigningStrategy,
  accountId: string,
  amount: number,
  tokenId: string,
  decimals: number,
): Promise<{ txId: string }> {
  const signerAccountId = strategy.getAccountId();
  const adjustedAmount = toRawAmount(amount, decimals);

  const transferTx = new TransferTransaction()
    .addTokenTransfer(tokenId, signerAccountId, -adjustedAmount)
    .addTokenTransfer(tokenId, accountId, adjustedAmount);

  const txResponse = await freezeSignExecute(transferTx, strategy);

  const client = strategy.getClient();
  await new TransactionReceiptQuery().setTransactionId(txResponse.transactionId).execute(client);

  return { txId: txResponse.transactionId.toString() };
}

export async function signScheduledTransaction(
  strategy: SigningStrategy,
  scheduleId: string,
): Promise<{ txId: string }> {
  const transaction = new ScheduleSignTransaction().setScheduleId(scheduleId);

  const txResponse = await freezeSignExecute(transaction, strategy);

  const client = strategy.getClient();
  await new TransactionReceiptQuery().setTransactionId(txResponse.transactionId).execute(client);

  return { txId: txResponse.transactionId.toString() };
}
